import React, { ReactElement, useContext, useEffect, useState } from 'react';
import { TextField, MenuItem, InputAdornment, Box, Typography, Select } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { gray40, gray100, white, gray60 } from '../../constants/colors';
import { getValidUsers } from '../../api/firebase/firestore/user';
import { GroupContext } from '../../contexts/group';

interface SearchFormProps {
  watchSearchItems: WatchSearchItemsType;
  setWatchSearchItems: React.Dispatch<React.SetStateAction<WatchSearchItemsType>>;
  groupId?: string;
}

export interface WatchSearchItemsType {
  debtorName: string | null;
  mansionName: string | null;
  establishmentStartDate: dayjs.Dayjs | null;
  establishmentEndDate: dayjs.Dayjs | null;
  responsiblePerson: string | null;
  startDate: dayjs.Dayjs | null;
  endDate: dayjs.Dayjs | null;
}

const AiOcrSearchForm = (props: SearchFormProps): ReactElement => {
  const { watchSearchItems, setWatchSearchItems, groupId } = props;
  const { groups } = useContext(GroupContext);

  const inputBaseStyle = {
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: gray60,
      borderWidth: '1px',
    },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: gray100,
    },
  };

  const styles = {
    searchFormBox: {
      gap: 2,
      padding: 2,
      border: `1px solid ${gray40}`,
      borderRadius: 1,
      backgroundColor: white,
    },
    searchForms: {
      flexDirection: 'column',
    },
    searchFromSortOrder: {
      display: 'flex',
      flexDirection: 'row',
      gap: 2,
      flexWrap: 'wrap',
      justifyContent: 'flex-start',
    },
    formWidth: {
      minWidth: '400px',
      textAlign: 'left',
      '@media screen and (max-width:960px)': {
        minWidth: '100%',
      },
      ...inputBaseStyle,
    },
    datePickerRootBox: {
      minWidth: '400px',
      '@media screen and (max-width:960px)': {
        width: '100%',
      },
    },
    datePickerBox: {
      display: 'flex',
      alignItems: 'center',
      gap: 1,
    },
    datePickerWidth: {
      width: '154px',
      ...inputBaseStyle,
      '@media screen and (max-width: 960px)': {
        width: '100%',
      },
    },
    datePickerTextBox: {
      display: 'flex',
      alignItems: 'center',
      flexDirection: 'column',
      width: '53px',
      '@media screen and (max-width:960px)': {
        display: 'none',
      },
    },
    datePickerTextBoxSp: {
      display: 'none',
      '@media screen and (max-width:960px)': {
        textAlign: 'left',
        display: 'block',
        mb: '3px',
      },
    },
    selectFiled: {
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: gray60,
        borderWidth: '1px',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: gray100,
        borderWidth: '1px',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: gray100,
        borderWidth: '1px',
      },
      minWidth: '400px',
      textAlign: 'left',
      '@media screen and (max-width:960px)': {
        minWidth: '100%',
      },
    },
  };

  // 検索用担当者リスト
  interface UserListItem {
    value: string;
    label: string;
  }
  const [userList, setUserList] = useState<Array<UserListItem>>([]);

  useEffect(() => {
    void (async (): Promise<void> => {
      const allUsers = await getValidUsers();

      if (groupId) {
        // グループ画面: GroupContextからメンバー情報を取得（API呼び出し不要）
        const group = groups.find(g => g.groupId === groupId);
        const memberUids = group?.memberUids ?? [];
        const filteredUsers = allUsers.filter(user => memberUids.includes(user.uid));
        setUserList(
          filteredUsers.map(user => ({ value: user.uid as string, label: user.name as string })),
        );
      } else {
        // 個人画面: 全ユーザー表示
        setUserList(
          allUsers.map(user => ({ value: user.uid as string, label: user.name as string })),
        );
      }
    })();
  }, [groupId, groups]);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={styles.searchFormBox}>
        <Box sx={styles.searchForms}>
          <Box sx={styles.searchFromSortOrder}>
            {/* 所有者名 */}
            <TextField
              value={watchSearchItems.debtorName ?? ''}
              sx={styles.formWidth}
              onChange={(e): void =>
                setWatchSearchItems({ ...watchSearchItems, debtorName: e.target.value })
              }
              placeholder="債務者の氏名"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />

            {/* 物件名 */}
            <TextField
              value={watchSearchItems.mansionName ?? ''}
              sx={styles.formWidth}
              onChange={(e): void =>
                setWatchSearchItems({ ...watchSearchItems, mansionName: e.target.value })
              }
              placeholder="マンション名"
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
              }}
              size="small"
            />

            <Box sx={styles.datePickerRootBox}>
              {/* 設定年月日日付範囲 */}
              <Typography sx={styles.datePickerTextBoxSp} fontSize={10}>
                原因の設定年月日
              </Typography>
              <Box sx={styles.datePickerBox}>
                <Box sx={styles.datePickerTextBox}>
                  <Typography fontSize={10}>原因の</Typography>
                  <Typography fontSize={10}>設定年月日</Typography>
                </Box>
                <DatePicker
                  sx={styles.datePickerWidth}
                  value={watchSearchItems.establishmentStartDate}
                  format="YYYY/MM/DD"
                  onChange={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({
                        ...watchSearchItems,
                        establishmentStartDate: newValue,
                      });
                    }
                  }}
                  onAccept={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({
                        ...watchSearchItems,
                        establishmentStartDate: newValue,
                      });
                    }
                  }}
                  slots={{
                    textField: params => (
                      <TextField
                        {...params}
                        placeholder={dayjs().startOf('month').format('YYYY/MM/DD')}
                        size="small"
                      />
                    ),
                  }}
                />
                <Typography>〜</Typography>
                <DatePicker
                  sx={styles.datePickerWidth}
                  value={watchSearchItems.establishmentEndDate}
                  format="YYYY/MM/DD"
                  onChange={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({ ...watchSearchItems, establishmentEndDate: newValue });
                    }
                  }}
                  onAccept={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({ ...watchSearchItems, establishmentEndDate: newValue });
                    }
                  }}
                  slots={{
                    textField: params => (
                      <TextField
                        {...params}
                        placeholder={dayjs().format('YYYY/MM/DD')}
                        size="small"
                      />
                    ),
                  }}
                />
              </Box>
            </Box>

            {/* 担当者 */}
            <Select
              displayEmpty
              sx={styles.selectFiled}
              value={watchSearchItems.responsiblePerson ?? ''}
              onChange={(e): void =>
                setWatchSearchItems({ ...watchSearchItems, responsiblePerson: e.target.value })
              }
              size="small"
              renderValue={(selected: string): ReactElement | undefined => {
                const selectedUser = userList.find(user => user.value === selected);
                if (selected === '') {
                  return <em style={{ color: gray60 }}>担当者を選択</em>;
                } else {
                  return <em>{selectedUser?.label}</em>;
                }
              }}
            >
              {/* プレースホルダーとして表示 */}
              <MenuItem value="">
                {' '}
                <em>担当者を選択</em>
              </MenuItem>
              {userList.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>

            <Box sx={styles.datePickerRootBox}>
              {/* 日付範囲 */}
              <Typography sx={styles.datePickerTextBoxSp} fontSize={10}>
                アップロード日
              </Typography>
              <Box sx={styles.datePickerBox}>
                <Box sx={styles.datePickerTextBox}>
                  <Typography fontSize={10}>アップ</Typography>
                  <Typography fontSize={10}>ロード日</Typography>
                </Box>
                <DatePicker
                  sx={styles.datePickerWidth}
                  value={watchSearchItems.startDate}
                  format="YYYY/MM/DD"
                  onChange={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({ ...watchSearchItems, startDate: newValue });
                    }
                  }}
                  onAccept={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({ ...watchSearchItems, startDate: newValue });
                    }
                  }}
                  slots={{
                    textField: params => (
                      <TextField
                        {...params}
                        placeholder={dayjs().startOf('month').format('YYYY/MM/DD')}
                        size="small"
                      />
                    ),
                  }}
                />
                <Typography>〜</Typography>
                <DatePicker
                  sx={styles.datePickerWidth}
                  value={watchSearchItems.endDate}
                  format="YYYY/MM/DD"
                  onChange={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({ ...watchSearchItems, endDate: newValue });
                    }
                  }}
                  onAccept={(newValue): void => {
                    if (newValue && newValue.isValid()) {
                      setWatchSearchItems({ ...watchSearchItems, endDate: newValue });
                    }
                  }}
                  slots={{
                    textField: params => (
                      <TextField
                        {...params}
                        placeholder={dayjs().format('YYYY/MM/DD')}
                        size="small"
                      />
                    ),
                  }}
                />
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default AiOcrSearchForm;
